import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";
import { ArrowRight } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { CardLogo } from "./CardLogo";
import { CardSkeleton } from "./CardSkeleton";

type Card = Database["public"]["Tables"]["cards"]["Row"];

interface PopularCardsSectionProps {
  onViewDetails: (slug: string) => void;
  onViewAllCards?: () => void;
  currentPage?: string;
  /** Landing/tool pages always show the module above the footer,
   *  regardless of the per-page visibility list in the admin settings. */
  forceVisible?: boolean;
}

interface SectionSettings {
  enabled: boolean;
  heading: string;
  subtitle: string;
  cards_count: number;
  button_text: string;
  show_on_pages: string[];
}

export function PopularCardsSection({
  onViewDetails,
  onViewAllCards,
  currentPage = "homepage",
  forceVisible = false,
}: PopularCardsSectionProps) {
  const [popularCards, setPopularCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<SectionSettings>({
    enabled: true,
    heading: "Beliebte Kreditkarten",
    subtitle: "Entdecken Sie die beliebtesten Kreditkarten deutscher Verbraucher",
    cards_count: 12,
    button_text: "Alle Kreditkarten ansehen",
    show_on_pages: ["homepage", "article-listing"],
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (settings.cards_count) {
      fetchPopularCards();
    }
  }, [settings.cards_count]);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from("page_settings")
        .select("settings")
        .eq("page_type", "popular_cards_section")
        .maybeSingle();

      if (error) throw error;

      if (data?.settings) {
        const fetchedSettings = data.settings as Partial<SectionSettings>;
        setSettings((prev) => ({ ...prev, ...fetchedSettings }));
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const fetchPopularCards = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("cards")
        .select("*")
        .eq("status", "published")
        .eq("is_popular", true)
        .order("boost_priority", { ascending: false })
        .limit(settings.cards_count);

      if (error) throw error;
      setPopularCards(data || []);
    } catch (error) {
      console.error("Error fetching popular cards:", error);
    } finally {
      setLoading(false);
    }
  };

  // Don't show section if disabled, not on current page, or no popular cards
  if (!settings.enabled || (!forceVisible && !settings.show_on_pages.includes(currentPage))) {
    return null;
  }

  if (!loading && popularCards.length === 0) {
    return null;
  }

  return (
    <section className="bg-gray-50 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">{settings.heading}</h2>
          <p className="text-lg text-gray-600">{settings.subtitle}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8 mx-auto max-w-fit items-start">
          {loading
            ? // Show skeletons during loading based on cards_count
              Array.from({ length: settings.cards_count }).map((_, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl p-4 self-stretch">
                  <div className="aspect-[3/2] bg-gray-100 rounded-lg mb-3" />
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded-sm w-3/4 mx-auto" />
                    <div className="h-3 bg-gray-200 rounded-sm w-1/2 mx-auto" />
                  </div>
                </div>
              ))
            : popularCards.map((card) => (
                <TooltipProvider key={card.id}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => onViewDetails(card.slug)}
                        aria-label={`Vis detaljer for ${card.name}`}
                        className="group bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg hover:border-blue-300 transition-all duration-300 w-full h-full flex flex-col"
                      >
                        <div className="aspect-[3/2] bg-gray-100 rounded-lg overflow-hidden mb-3">
                          <CardLogo
                            logoUrl={card.logo_url}
                            bankName={card.issuer}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            width={192}
                            height={128}
                          />
                        </div>
                        <div className="text-center">
                          <p className="text-gray-900 text-sm font-medium mb-1 line-clamp-1">{card.name}</p>
                          <p className="text-gray-600 text-xs">{card.issuer}</p>
                        </div>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {card.name} – {card.apr_effective?.toFixed(2)} % eff. Jahreszins
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
        </div>

        <div className="text-center">
          <button
            onClick={onViewAllCards}
            aria-label="Se alle kredittkort"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium transition-colors"
          >
            {settings.button_text}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
